import { useSelector } from 'react-redux';
import Global from '../../../../../../store/global-selectors';
import { useDoesSellerHavePromotionalOffer } from './use-does-seller-have-promotional-offer';
import { useGetAdditionalUsersCountForPromoPlan } from './use-get-additional-users-count-for-promo-plan';
import { getPlanTotalPrice, getPromotionalPricePerMonth, getPromotionalShipmentLimit } from '../helpers';
import { LegacyAddOnsToPreserveThroughMigration } from '../constants';

/** @typedef {import('../types.d.ts').PlanDetails} PlanDetails */
/** @typedef {import('../types.d.ts').Price} Price */
/** @typedef {import('../types.d.ts').ShipmentLimitTier} ShipmentLimitTier */
/** @typedef {import('../types.d.ts').PromotionalMigrationResponse} PromotionalMigrationResponse */

/**
 * @param {object} args
 * @param {PromotionalMigrationResponse} args.promotionDetails
 * @param {PlanDetails} args.currentPlanDetails
 * @param {number} args.additionalUserCount
 * @param {Price} args.additionalUserPrice
 * @param {number} args.legacyLineItemAddOnsPrice
 * @returns {PlanDetails}
 */
const convertResultCallToPlanDetails = ({
  additionalUserCount,
  additionalUserPrice,
  currentPlanDetails,
  legacyLineItemAddOnsPrice,
  promotionDetails,
}) => {
  const { targetBillingPlan, targetCoupon } = promotionDetails || {};

  if (!targetBillingPlan || !targetCoupon) {
    return null; // user is not targeted for a promotional migration
  }

  const shipmentLimit = getPromotionalShipmentLimit(targetBillingPlan.shipmentLimit.tiers, currentPlanDetails);

  const totalPricePerMonth = getPlanTotalPrice({
    basePlanPrice: targetBillingPlan.pricePerPeriod,
    shipmentLimitPrice: shipmentLimit.price.value,
    additionalUserCount,
    additionalUserPrice: additionalUserPrice.value,
    legacyLineItemAddOnsPrice,
  });

  const userLimit = {
    ...targetBillingPlan.userLimit,
    limit: targetBillingPlan.userLimit.limit + additionalUserCount,
  };

  return {
    billingPlanId: targetBillingPlan.billingPlanId,
    billingPlanCode: targetBillingPlan.code,
    name: targetBillingPlan.name,
    pricePerMonth: totalPricePerMonth,
    promotionalPricePerMonth: getPromotionalPricePerMonth(totalPricePerMonth, targetCoupon),
    userLimit,
    shipmentLimit,
    discountPercentage: targetCoupon.discountPercent / 100,
    offerAppliesForMonths: targetCoupon.appliesForMonths,
  };
};

/**
 * Returns the per-seat price of an additional user for the promotional plan
 * @returns {Price}
 */
const useGetPromotionalPlanAdditionalUserPrice = () =>
  useSelector((state) => Global.selectSellerPromotionUpgrade(state).targetBillingPlan?.userAddOnPrice ?? { value: 0 });

/**
 * Returns the total cost of the few addOns that will be additionally-billed line-items once the user migrates.
 * @returns {number}
 */
const useGetLegacyLineItemAddOnsPrice = () => {
  return useSelector((state) => {
    const { marketplaceAddOns = [] } = Global.selectSellerSubscriptionPlanInfo(state) || {};
    const legacyAddOnsThatWillAffectPromoPrice = marketplaceAddOns.filter(
      (addOn) => addOn.isActive && LegacyAddOnsToPreserveThroughMigration.includes(addOn.code),
    );
    const totalAddOnsPrice = legacyAddOnsThatWillAffectPromoPrice.reduce((acc, addOn) => acc + addOn.price?.value || 0, 0);
    return totalAddOnsPrice || 0;
  });
};

/**
 *
 * @param {PlanDetails} currentPlanDetails
 * @returns {PlanDetails | null}
 */
export const useGetPromotionalPlanDetails = (currentPlanDetails) => {
  const hasPromotionalOffer = useDoesSellerHavePromotionalOffer();
  const additionalUserCount = useGetAdditionalUsersCountForPromoPlan();
  const additionalUserPrice = useGetPromotionalPlanAdditionalUserPrice();
  const legacyLineItemAddOnsPrice = useGetLegacyLineItemAddOnsPrice();
  const promotionDetails = useSelector(Global.selectSellerPromotionUpgrade);
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const isOnAnnualBilling = subscriptionData?.currentSubscription?.isOnAnnualBilling ?? false;

  if (!hasPromotionalOffer) {
    return null;
  }

  if (isOnAnnualBilling) {
    const allAvailablePlans = [...(subscriptionData?.allPlans ?? []), ...(subscriptionData?.defaultProfilePlans ?? [])];
    const hasAnnualPlanWithPromotion = allAvailablePlans.some(
      (plan) => plan.isOnAnnualBilling && plan.bestActivePromotion?.coupon?.discountPercent,
    );

    if (!hasAnnualPlanWithPromotion) {
      return null;
    }
  }

  return convertResultCallToPlanDetails({
    additionalUserCount,
    additionalUserPrice,
    currentPlanDetails,
    legacyLineItemAddOnsPrice,
    promotionDetails,
  });
};
